import Big from 'big.js';

import { isValidNumber, isZero } from '../number';
import { getSize } from '../iterable/getSize';

import { ERRORS, MAX_BARCODE_LENGTH, MAX_INTERNATIONAL_PRICE, MAX_PRICE, MAX_QUANTITY, MAX_RETAIL_WHOLESALE_RATIO, MAX_SKU_LENGTH, MIN_INTERNATIONAL_PRICE, MIN_PRICE, MIN_RETAIL_WHOLESALE_RATIO, MIN_QUANTITY } from '../../constants/validations';
import { EBAY, ETSY, SHOPIFY } from '../../constants/channels';
import { CURRENCY } from '../../constants/product';

export function getBarcodeError({ channel, value }) {
  if (!value) return false;

  if (MAX_BARCODE_LENGTH.hasOwnProperty(channel) && getSize(value) > MAX_BARCODE_LENGTH[channel]) {
    return ERRORS.BARCODE.LENGTH_EXCEEDED[channel];
  }

  return false;
}

export function getCAPError({ channel, value }) {
  if (!isValidNumber(value)) return false;

  const CAPNumber = new Big(value);

  if (CAPNumber.eq(0)) return false;

  if (MIN_PRICE.hasOwnProperty(channel) && new Big(MIN_PRICE[channel]).gt(CAPNumber)) {
    return ERRORS.CAP.TOO_LOW[channel];
  }

  if (MAX_PRICE.hasOwnProperty(channel) && new Big(MAX_PRICE[channel]).lte(CAPNumber)) {
    return ERRORS.CAP.TOO_HIGH[channel];
  }

  return false;
}

export function getCPIError({ channel, value }) {
  if (!isValidNumber(value)) return false;

  const CPINumber = new Big(value);

  if (MIN_PRICE.hasOwnProperty(channel) && new Big(MIN_PRICE[channel]).gt(CPINumber)) {
    return ERRORS.CPI.TOO_LOW[channel];
  }

  if (MAX_PRICE.hasOwnProperty(channel) && new Big(MAX_PRICE[channel]).lte(CPINumber)) {
    return ERRORS.CPI.TOO_HIGH[channel];
  }

  return false;
}

export function getGTINError({ value }) {
  if (!value) return false;

  if (!/^(\d{8}|\d{12}|\d{13}|\d{14})$/.test(value)) {
    return ERRORS.GTIN.INVALID;
  }

  return false;
}

export function getPriceError({ allowEmpty, channel, value }) {
  switch (channel) {
    case EBAY:
    case ETSY: {
      if (!isValidNumber(value)) {
        return !allowEmpty && ERRORS.PRICE.REQUIRED;
      }

      break;
    }

    default: {
      if (!isValidNumber(value)) {
        return false;
      }

      break;
    }
  }

  const priceNumber = new Big(value);

  if (MIN_PRICE.hasOwnProperty(channel) && new Big(MIN_PRICE[channel]).gt(priceNumber)) {
    return ERRORS.PRICE.TOO_LOW[channel];
  }

  if (MAX_PRICE.hasOwnProperty(channel) && new Big(MAX_PRICE[channel]).lte(priceNumber)) {
    return ERRORS.PRICE.TOO_HIGH[channel];
  }

  return false;
}

export function getQuantityError({ allowEmpty, allowZero, channel, value }) {
  switch (channel) {
    case EBAY: {
      if (!isValidNumber(value)) {
        return !allowEmpty && ERRORS.QUANTITY.REQUIRED;
      }

      break;
    }

    case ETSY: {
      if (!isValidNumber(value)) {
        return !allowEmpty && ERRORS.QUANTITY.REQUIRED;
      }

      if (isZero(value)) {
        return !allowZero && ERRORS.QUANTITY.MUST_BE_IN_STOCK;
      }

      break;
    }

    default: {
      if (!isValidNumber(value)) {
        return false;
      }

      break;
    }
  }

  const quantityNumber = new Big(value);

  if (!quantityNumber.eq(quantityNumber.round(0, 0))) {
    return ERRORS.QUANTITY.INVALID;
  }

  if (MIN_QUANTITY.hasOwnProperty(channel) && new Big(MIN_QUANTITY[channel]).gt(quantityNumber)) {
    return ERRORS.QUANTITY.TOO_LOW[channel];
  }

  if (MAX_QUANTITY.hasOwnProperty(channel) && new Big(MAX_QUANTITY[channel]).lte(quantityNumber)) {
    return ERRORS.QUANTITY.TOO_HIGH[channel];
  }

  return false;
}

export function getSKUError({ allowEmpty, channel, value }) {
  switch (channel) {
    case EBAY: {
      if (!value) return !allowEmpty && ERRORS.SKU.REQUIRED;

      if (MAX_SKU_LENGTH.hasOwnProperty(channel) && getSize(value) > MAX_SKU_LENGTH[channel]) {
        return ERRORS.SKU.LENGTH_EXCEEDED[channel];
      }

      return false;
    }

    case ETSY: {
      if (!value) return false;

      if (MAX_SKU_LENGTH.hasOwnProperty(channel) && getSize(value) > MAX_SKU_LENGTH[channel]) {
        return ERRORS.SKU.LENGTH_EXCEEDED[channel];
      }

      if (/[\^$`]/.test(value)) {
        return ERRORS.SKU.FORBIDDEN_CHARACTERS[channel];
      } else if (!/^[\p{L}\p{Nd}]/u.test(value)) {
        return ERRORS.SKU.ALPHANUMERIC_BEGIN;
      } else {
        return false;
      }
    }

    case SHOPIFY: {
      if (!value) return false;

      if (MAX_SKU_LENGTH.hasOwnProperty(channel) && getSize(value) > MAX_SKU_LENGTH[channel]) {
        return ERRORS.SKU.LENGTH_EXCEEDED[channel];
      }

      return false;
    }

    default: {
      return false;
    }
  }
}

export function getRetailPriceError({ allowEmpty, allowZero, currency, retail, wholesale }) {
  if (!isValidNumber(retail)) {
    return (
      (isValidNumber(wholesale) || (!allowEmpty && currency === CURRENCY.USD)) &&
      ERRORS.INTERNATIONAL_PRICE.RETAIL.REQUIRED
    );
  }

  if (isZero(retail) && allowZero) {
    return false;
  }

  const retailNumber = new Big(retail);

  if (MIN_INTERNATIONAL_PRICE.hasOwnProperty(currency) && new Big(MIN_INTERNATIONAL_PRICE[currency]).gt(retailNumber)) {
    return ERRORS.INTERNATIONAL_PRICE.RETAIL.INVALID[currency];
  }

  if (MAX_INTERNATIONAL_PRICE.hasOwnProperty(currency) && new Big(MAX_INTERNATIONAL_PRICE[currency]).lt(retailNumber)) {
    return ERRORS.INTERNATIONAL_PRICE.RETAIL.INVALID[currency];
  }

  if (isValidNumber(wholesale)) {
    const wholesaleNumber = new Big(wholesale);

    if (wholesaleNumber.gt(0)) {
      const diff = retailNumber.div(wholesaleNumber);
      if (diff.lt(MIN_RETAIL_WHOLESALE_RATIO) || diff.gt(MAX_RETAIL_WHOLESALE_RATIO)) {
        return ERRORS.INTERNATIONAL_PRICE.RETAIL_WHOLESALE_RATIO;
      }
    }
  }

  return false;
}

export function getWholesalePriceError({ allowEmpty, allowZero, currency, retail, wholesale }) {
  if (!isValidNumber(wholesale)) {
    return (
      (isValidNumber(retail) || (!allowEmpty && currency === CURRENCY.USD)) &&
      ERRORS.INTERNATIONAL_PRICE.WHOLESALE.REQUIRED
    );
  }

  if (isZero(wholesale) && allowZero) {
    return false;
  }

  const wholesaleNumber = new Big(wholesale);

  if (MIN_INTERNATIONAL_PRICE.hasOwnProperty(currency) && new Big(MIN_INTERNATIONAL_PRICE[currency]).gt(wholesale)) {
    return ERRORS.INTERNATIONAL_PRICE.WHOLESALE.INVALID[currency];
  }

  if (MAX_INTERNATIONAL_PRICE.hasOwnProperty(currency) && new Big(MAX_INTERNATIONAL_PRICE[currency]).lt(wholesale)) {
    return ERRORS.INTERNATIONAL_PRICE.WHOLESALE.INVALID[currency];
  }

  if (isValidNumber(retail)) {
    const retailNumber = new Big(retail);

    if (retailNumber.gt(0)) {
      const diff = retailNumber.div(wholesaleNumber);

      if (diff.lt(MIN_RETAIL_WHOLESALE_RATIO) || diff.gt(MAX_RETAIL_WHOLESALE_RATIO)) {
        return ERRORS.INTERNATIONAL_PRICE.RETAIL_WHOLESALE_RATIO;
      }
    }
  }

  return false;
}
